import axios from "axios";

const trimTrailingSlash = (value) => {
  if (!value) return value;
  return value.endsWith("/") ? value.slice(0, -1) : value;
};

export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return trimTrailingSlash(process.env.REACT_APP_API_URL);
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (
      hostname.includes("ondigitalocean.app") ||
      hostname.includes("services.knls.in") ||
      hostname === "loksy.in" ||
      hostname === "www.loksy.in"
    ) {
      return "/api";
    }
    // CRA dev server proxy (package.json → localhost:3000) only applies to same-origin /api
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "/api";
    }
  }

  return "http://localhost:3000/api";
};

export const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  const keys = [
    "admin_token",
    "user_token",
    "token",
    "adminToken",
    "userToken",
    "access_token",
  ];
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of keys) {
      const value = storage.getItem(key);
      if (
        value &&
        value !== "null" &&
        value !== "undefined" &&
        value.trim() !== ""
      ) {
        return value;
      }
    }
  }
  return null;
};

export const getAuthHeader = () => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const clearStoredAuth = () => {
  if (typeof window === "undefined") return;
  const keys = [
    "token",
    "admin_token",
    "user_token",
    "admin_user",
    "user_user",
    "user",
    "admin_devUser",
    "user_devUser",
    "devUser",
    "adminToken",
    "userToken",
    "access_token",
    "auth_token",
    "authToken",
    "jwt",
  ];
  for (const storage of [localStorage, sessionStorage]) {
    keys.forEach((key) => {
      storage.removeItem(key);
      const tabScopedKey = Array.from({ length: storage.length })
        .map((_, index) => storage.key(index))
        .find(
          (storedKey) =>
            typeof storedKey === "string" &&
            keys.some((authKey) => storedKey.startsWith(`${authKey}::__tab_`)),
        );
      if (tabScopedKey) {
        storage.removeItem(tabScopedKey);
      }
    });
  }
};

const RETRYABLE_STATUS = [408, 425, 429, 500, 502, 503, 504];

const isRetryableError = (error) => {
  // Never retry a request the caller explicitly cancelled.
  if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") return false;

  const method = (error?.config?.method || "get").toLowerCase();
  // Only auto-retry idempotent reads. Retrying POST/PUT/DELETE could double-write.
  if (method !== "get" && method !== "head") return false;

  // No response => network error / timeout / connection reset (the cold-start case).
  if (!error?.response) return true;

  return RETRYABLE_STATUS.includes(error.response.status);
};

/**
 * Attach automatic retry with exponential backoff for transient failures.
 * This is what makes screens recover from a cold backend without the user
 * having to hit "Retry" manually.
 */
export const attachRetryInterceptor = (client, { retries = 3, baseDelay = 600 } = {}) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      if (!config || !isRetryableError(error)) {
        return Promise.reject(error);
      }

      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount >= retries) {
        return Promise.reject(error);
      }

      config.__retryCount += 1;
      // Exponential backoff with jitter: ~0.6s, ~1.2s, ~2.4s
      const delay =
        baseDelay * Math.pow(2, config.__retryCount - 1) + Math.random() * 200;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return client(config);
    },
  );
  return client;
};

export const createApiClient = (baseURL) => {
  const client = axios.create({
    baseURL,
    timeout: 30000,
  });

  attachRetryInterceptor(client);

  client.interceptors.request.use((config) => {
    const header = getAuthHeader();
    if (header.Authorization) {
      config.headers.Authorization = header.Authorization;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && typeof window !== "undefined") {
        clearStoredAuth();
        const path = window.location.pathname;
        const isUserPath = path.startsWith("/user/");
        window.location.href = isUserPath ? "/user-login" : "/aalogin";
      }
      return Promise.reject(error);
    },
  );

  return client;
};
